import {
    Dialog,
    DialogContent,
    DialogNavbar,
    DialogTrigger,
} from "@/components/ui/dialog";
import FeatureFlag from "@/domain/enums/FeatureFlag";
import UserType from "@/domain/enums/UserType";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import useOrganization from "@/hooks/useOrganization";
import { cn } from "@/lib/utils";
import LicenseSelector from "@/presentation/license/LicenseSelector";
import LicenseTabs from "@/presentation/license/licenseTabs";
import { setOpenedEvent } from "@/stores/eventStore";
import settingsStore, {
    setSettingsDialogOpen,
    setSettingsDialogTarget,
    SettingsMenu,
} from "@/stores/settingsStore";
import { useFeatureFlag } from "@cs_teamwaydev/mailvista-sdk_react";
import { useSelector } from "@tanstack/react-store";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LicensePrincingCardVariants } from "../cards/LicensePrincingCard";
import CenteredSpinner from "../CenteredSpinner";
import { Button } from "../ui/button";
import UserAvatar from "../UserAvatar";
import MyAccountSettings from "./account/MyAccountSettings";
import HomepageSettings from "./homepage/HomepageSettings";
import LegalPagesSettings from "./legalPages/LegalPagesSettings";
import ModelManagementSettings from "./modelManagement/ModelManagementSettings";
import ResourcesSettings from "./resources/ResourcesSettings";
import SignatureSettings from "./signature/SignatureSettings";
import UserManagementSettings from "./userManagement/UserManagementSettings";

type SettingsDialogProps = {
    trigger: ReactNode;
};

const SettingsDialog = ({ trigger }: SettingsDialogProps) => {
    const { t } = useTranslation();
    const isLicenseManagementDisplayed = useFeatureFlag(
        FeatureFlag.SETTINGS_DISPLAY_LICENSEMANAGEMENT,
    );
    const isModelManagementDisplayed = useFeatureFlag(
        FeatureFlag.SETTINGS_DISPLAY_MODELMANAGEMENT,
    );
    const isSignatureSettingsTabVisible = useFeatureFlag(
        FeatureFlag.SETTINGS_DISPLAY_SIGNATURE,
    );

    const { data: user } = useGetUserProfile();
    const settingsDialogOpen = useSelector(
        settingsStore,
        (state) => state.settingsDialogOpen,
    );

    const { currentUserIsAdmin } = useOrganization({
        fetchUsers: settingsDialogOpen,
    });
    const settingsDialogTarget = useSelector(
        settingsStore,
        (state) => state.settingsDialogTarget,
    );

    const [currentTab, setCurrentTab] = useState<SettingsMenu>(
        settingsDialogTarget || SettingsMenu.Account,
    );

    useEffect(() => {
        setCurrentTab(settingsDialogTarget || SettingsMenu.Account);
    }, [settingsDialogTarget]);

    const SettingsTabs = [
        {
            id: SettingsMenu.Account,
            label: "settings.navigation.account",
            component: <MyAccountSettings />,
        },
        ...(currentUserIsAdmin
            ? [
                  {
                      id: SettingsMenu.UserManagement,
                      label: "settings.navigation.users",
                      icon: "👥",
                      component: <UserManagementSettings />,
                  },
              ]
            : []),
        ...(isModelManagementDisplayed
            ? [
                  {
                      id: SettingsMenu.ModelManagement,
                      label: "settings.navigation.modelManagement",
                      icon: "⚙️",
                      component: <ModelManagementSettings />,
                  },
              ]
            : []),
        {
            id: SettingsMenu.Homepage,
            label: "settings.navigation.homepage",
            icon: "🎨",
            component: <HomepageSettings />,
        },
        ...(isSignatureSettingsTabVisible
            ? [
                  {
                      id: SettingsMenu.EmailSignature,
                      label: "settings.navigation.emailSignature",
                      icon: "✍️",
                      component: <SignatureSettings />,
                  },
              ]
            : []),
        // {
        //     id: SettingsMenu.Notifications,
        //     label: 'settings.navigation.notifications',
        //     component: <NotificationsSettings />,
        // }, TODO: Add notifications settings when SDK is ready
        ...(isLicenseManagementDisplayed
            ? [
                  {
                      id: SettingsMenu.MyLicense,
                      label: "settings.navigation.myLicense",
                      icon: "🪪​",
                      component: (
                          <LicenseSelector
                              defaultTab={
                                  user?.userType === UserType.ENTERPRISE_USER ||
                                  user?.userType === UserType.ENTERPRISE_MANAGER
                                      ? LicenseTabs.ENTERPRISE
                                      : LicenseTabs.PUBLIC
                              }
                              variant={LicensePrincingCardVariants.Compact}
                          />
                      ),
                  },
              ]
            : []),
        {
            id: SettingsMenu.Resources,
            label: "settings.navigation.resources",
            icon: "📚",
            component: <ResourcesSettings />,
        },
        {
            id: SettingsMenu.LegalPages,
            label: "settings.navigation.legalPages",
            icon: "🔑",
            component: <LegalPagesSettings />,
        },
    ];

    useEffect(
        () => () => {
            setSettingsDialogOpen(false);
            setSettingsDialogTarget(null);
        },
        [],
    );

    if (!user) {
        return <CenteredSpinner />;
    }

    return (
        <Dialog
            onOpenChange={(open) => {
                setSettingsDialogOpen(open);
                setSettingsDialogTarget(null);
                if (open) {
                    setOpenedEvent(undefined);
                }
            }}
            open={settingsDialogOpen}
        >
            <DialogTrigger>{trigger}</DialogTrigger>
            <DialogContent
                className="h-[90vh] max-h-[90vh]"
                onInteractOutside={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest(".didomi-consent-popup-preferences")) {
                        e.preventDefault();
                    }
                }}
                onPointerDownOutside={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest(".didomi-consent-popup-preferences")) {
                        e.preventDefault();
                    }
                }}
                variant="navbar"
            >
                <DialogNavbar>
                    {SettingsTabs.filter(
                        (tab) => tab.id === SettingsMenu.Account,
                    ).map((tab) => (
                        <div key={tab.id}>
                            <Button
                                className={cn(
                                    "flex h-auto items-center gap-4 whitespace-normal border-[1px] border-surface-secondary hover:border-surface-active hover:bg-surface-active hover:text-white",
                                    currentTab === tab.id &&
                                        "border-surface-active bg-surface-active text-white",
                                )}
                                onClick={() => setCurrentTab(tab.id)}
                                variant="menuItem"
                            >
                                <UserAvatar user={user} />
                                <div className="flex flex-col">
                                    <p className="break-words text-left text-sm font-medium">
                                        {user.profile.displayName}
                                    </p>
                                    <p className="text-left text-xs text-gray-400">
                                        {t(tab.label)}
                                    </p>
                                </div>
                            </Button>
                        </div>
                    ))}
                    <ul className="mt-8 flex flex-col gap-1">
                        {SettingsTabs.filter(
                            (tab) => tab.id !== SettingsMenu.Account,
                        ).map((tab) => (
                            <li key={tab.id}>
                                <Button
                                    className={cn(
                                        "flex items-start whitespace-normal px-2 py-1 text-left",
                                        currentTab === tab.id &&
                                            "text-wrap border-surface-primary bg-surface-secondary",
                                    )}
                                    onClick={() => setCurrentTab(tab.id)}
                                    size="blank"
                                    variant="menuItem"
                                >
                                    {tab.icon && (
                                        <span className="text-sm">
                                            {tab.icon}
                                        </span>
                                    )}
                                    {t(tab.label)}
                                </Button>
                            </li>
                        ))}
                    </ul>
                </DialogNavbar>
                <div className="min-h-0 w-full overflow-y-auto px-4 py-8">
                    {
                        SettingsTabs.find((tab) => tab.id === currentTab)
                            ?.component
                    }
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsDialog;
